import { useRef } from 'react';
import { Btn, Card, Grid } from '../ui';
import { OPERATOR_RESPONSE_TYPE_OPTIONS, OPERATOR_RESPONSE_TYPES } from '../../utils/constants';

type OperatorResponseOption = (typeof OPERATOR_RESPONSE_TYPE_OPTIONS)[number];

export type FormOperatorResponseTypeProps = {
    value?: string | null;
    onChange: (value: string) => void;
    options?: readonly OperatorResponseOption[];
};

const previewBtn = (
    text: string,
    variant: 'success-outlined' | 'danger-outlined' | 'secondary-outlined' | 'warning-outlined',
    buttonClasses?: string,
) => (
    <Btn
        buttonAs="span"
        buttonSize="sm"
        variant={variant}
        buttonText={text}
        buttonClasses={['response-type-preview-btn', buttonClasses].filter(Boolean).join(' ')}
        buttonAriaProps={{ 'aria-hidden': true }}
    />
);

const renderResponseTypePreview = (responseType: string) => {
    switch (responseType) {
        case OPERATOR_RESPONSE_TYPES.YES_NO:
            return (
                <div className="response-type-preview" aria-hidden="true">
                    {previewBtn('YES', 'success-outlined')}
                    {previewBtn('NO', 'danger-outlined')}
                </div>
            );
        case OPERATOR_RESPONSE_TYPES.ADDED_NOT_ADDED:
            return (
                <div className="response-type-preview" aria-hidden="true">
                    {previewBtn('Added', 'secondary-outlined', 'rtp-btn-added')}
                    {previewBtn('Not Added', 'success-outlined')}
                </div>
            );
        case OPERATOR_RESPONSE_TYPES.OK_CLEANED_REPLACED_NA:
            return (
                <div className="response-type-preview" aria-hidden="true">
                    {previewBtn('OK', 'success-outlined')}
                    {previewBtn('Cleaned', 'secondary-outlined', 'rtp-btn-cleaned')}
                    {previewBtn('Replaced', 'warning-outlined')}
                    {previewBtn('N/A', 'secondary-outlined')}
                </div>
            );
        case OPERATOR_RESPONSE_TYPES.OK_REPLACED_NA:
            return (
                <div className="response-type-preview" aria-hidden="true">
                    {previewBtn('OK', 'success-outlined')}
                    {previewBtn('Replaced', 'warning-outlined')}
                    {previewBtn('N/A', 'secondary-outlined')}
                </div>
            );
        case OPERATOR_RESPONSE_TYPES.FOUR_BUTTON:
            return (
                <div className="response-type-preview rtp-icons" aria-hidden="true">
                    {previewBtn('✓', 'success-outlined')}
                    {previewBtn('!', 'warning-outlined')}
                    {previewBtn('✕', 'danger-outlined')}
                    {previewBtn('N/A', 'secondary-outlined')}
                </div>
            );
        case OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY:
            return (
                <div className="response-type-preview rtp-qty" aria-hidden="true">
                    {previewBtn('Qty', 'secondary-outlined', 'rtp-btn-qty')}
                    {previewBtn('unit', 'secondary-outlined')}
                </div>
            );
        default:
            return null;
    }
};

export const FormOperatorResponseType = ({
    value,
    onChange,
    options = OPERATOR_RESPONSE_TYPE_OPTIONS,
}: FormOperatorResponseTypeProps) => {
    const selectedValue = value || options[0]?.value || OPERATOR_RESPONSE_TYPES.ADDED_NOT_ADDED;
    const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
    const selectedIndex = Math.max(
        0,
        options.findIndex((opt) => opt.value === selectedValue),
    );

    const focusOption = (index: number) => {
        const next = options[index];
        if (!next) return;
        onChange(next.value);
        requestAnimationFrame(() => optionRefs.current[index]?.focus());
    };

    const onOptionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
        const last = options.length - 1;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                focusOption(index === last ? 0 : index + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                focusOption(index === 0 ? last : index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusOption(0);
                break;
            case 'End':
                event.preventDefault();
                focusOption(last);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                onChange(options[index].value);
                break;
            default:
                break;
        }
    };

    return (
        <div className="item-details-section response-detail-section">
            <h4 className="item-section-label">Operator Response Type</h4>
            <Grid
                gridColumns={2}
                className="response-type-cards"
                role="radiogroup"
                aria-label="Operator response type"
            >
                {options.map((opt, index) => {
                    const selected = selectedValue === opt.value;
                    const selectType = () => onChange(opt.value);
                    return (
                        <Card
                            key={opt.value}
                            ref={(node) => {
                                optionRefs.current[index] = node;
                            }}
                            variant="card-sm"
                            role="radio"
                            aria-checked={selected}
                            tabIndex={index === selectedIndex ? 0 : -1}
                            cardClasses={`response-type-card ${selected ? 'active' : ''}`}
                            onClick={selectType}
                            onKeyDown={(event) => onOptionKeyDown(event, index)}
                        >
                            <span className="response-type-card-label">{opt.label}</span>
                            {renderResponseTypePreview(opt.value)}
                        </Card>
                    );
                })}
            </Grid>
        </div>
    );
};
